import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import { useChromeDirection } from '@/lib/i18n/useLocale';
import { cn } from './cn';
import { Button } from './Button';

/**
 * Composable filters, expressed as field/operator/value chips, with the whole
 * set living in the URL.
 *
 * The URL is the point: a filtered list can be linked to, bookmarked, reloaded and opened
 * in a second tab. A filter set is a query; a query has an address (PHASE 12 decision).
 *
 * Wire format, one `f` param per chip:
 *
 *   ?f=status:is:enrolled&f=name:contains:aya%20k&sort=created_at:desc
 *
 * The value is percent-encoded so it may contain the separator. Chips whose
 * field is not in `fields` are dropped on parse rather than rendered as unknown,
 * which is what makes a stale bookmark degrade instead of crash.
 */

export type FilterOperator =
  | 'is'
  | 'is_not'
  | 'contains'
  | 'before'
  | 'after'
  | 'is_empty'
  | 'is_not_empty';

/** Operators that take no value — the chip is complete without one. */
export const VALUELESS_OPERATORS: ReadonlySet<FilterOperator> = new Set<FilterOperator>([
  'is_empty',
  'is_not_empty',
]);

export type FilterFieldType = 'text' | 'select' | 'date' | 'number';

export interface FilterFieldDef {
  id: string;
  label: string;
  type: FilterFieldType;
  /** Required for `type: 'select'`; supplies both the control and the chip label. */
  options?: { value: string; label: string }[];
  /** Defaults to a sensible set for the field type. */
  operators?: FilterOperator[];
}

export interface Filter {
  field: string;
  operator: FilterOperator;
  value: string;
}

const DEFAULT_OPERATORS: Record<FilterFieldType, FilterOperator[]> = {
  text: ['contains', 'is', 'is_not', 'is_empty', 'is_not_empty'],
  select: ['is', 'is_not', 'is_empty', 'is_not_empty'],
  date: ['before', 'after', 'is'],
  number: ['is', 'is_not', 'before', 'after'],
};

export function operatorsFor(field: FilterFieldDef): FilterOperator[] {
  return field.operators ?? DEFAULT_OPERATORS[field.type];
}

// ------------------------------------------------------------ URL round-tripping

/** One chip → one `f` param value. */
export function serializeFilter(filter: Filter): string {
  const value = VALUELESS_OPERATORS.has(filter.operator) ? '' : encodeURIComponent(filter.value);
  return `${filter.field}:${filter.operator}:${value}`;
}

/**
 * One `f` param value → one chip, or `null` if it is malformed or names a field
 * this list does not have. Never throws: a URL is user input.
 */
export function parseFilter(raw: string, fields: FilterFieldDef[]): Filter | null {
  const separator = raw.indexOf(':');
  if (separator < 0) return null;
  const field = raw.slice(0, separator);
  const rest = raw.slice(separator + 1);
  const second = rest.indexOf(':');
  if (second < 0) return null;
  const operator = rest.slice(0, second) as FilterOperator;
  const rawValue = rest.slice(second + 1);

  const def = fields.find((f) => f.id === field);
  if (!def) return null;
  if (!operatorsFor(def).includes(operator)) return null;

  let value = '';
  if (!VALUELESS_OPERATORS.has(operator)) {
    try {
      value = decodeURIComponent(rawValue);
    } catch {
      // A malformed percent-escape is a broken link, not a crash.
      return null;
    }
    if (value === '') return null;
  }
  return { field, operator, value };
}

export function serializeFilters(filters: Filter[]): string[] {
  return filters.map(serializeFilter);
}

export function parseFilters(raw: string[], fields: FilterFieldDef[]): Filter[] {
  return raw.map((r) => parseFilter(r, fields)).filter((f): f is Filter => f !== null);
}

export const FILTER_PARAM = 'f';

/**
 * The filter set for this list, read from and written to the URL.
 *
 * Writing replaces the history entry rather than pushing one, so Back leaves the
 * list instead of stepping through every chip the user added. It also resets the
 * `page` param, because page 4 of a different query is not a place.
 */
export function useUrlFilters(fields: FilterFieldDef[]) {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = useMemo(() => searchParams.getAll(FILTER_PARAM), [searchParams]);
  const filters = useMemo(() => parseFilters(raw, fields), [raw, fields]);

  const setFilters = useCallback(
    (next: Filter[]) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          params.delete(FILTER_PARAM);
          for (const value of serializeFilters(next)) params.append(FILTER_PARAM, value);
          params.delete('page');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const addFilter = useCallback(
    (filter: Filter) => setFilters([...filters, filter]),
    [filters, setFilters],
  );
  const removeFilter = useCallback(
    (index: number) => setFilters(filters.filter((_, i) => i !== index)),
    [filters, setFilters],
  );
  const clearAll = useCallback(() => setFilters([]), [setFilters]);

  return { filters, setFilters, addFilter, removeFilter, clearAll };
}

// ------------------------------------------------------------------- the bar

function chipLabel(filter: Filter, fields: FilterFieldDef[], t: (k: string) => string): string {
  const def = fields.find((f) => f.id === filter.field);
  const fieldLabel = def?.label ?? filter.field;
  const operator = t(`ui.filterOp_${filter.operator}`);
  if (VALUELESS_OPERATORS.has(filter.operator)) return `${fieldLabel} ${operator}`;
  const value =
    def?.type === 'select'
      ? (def.options?.find((o) => o.value === filter.value)?.label ?? filter.value)
      : filter.value;
  return `${fieldLabel} ${operator} ${value}`;
}

export interface FilterBarProps {
  /**
   * The ad-hoc chip model: the fields a chip may filter on, the chips in force (normally
   * from `useUrlFilters`) and the handlers that change them. Omit all four on a list whose
   * endpoint filters through named parameters only — the bar is then the search region,
   * the home of the list's purpose-built controls (`children`) and its "Clear all".
   */
  fields?: FilterFieldDef[];
  filters?: Filter[];
  onAdd?: (filter: Filter) => void;
  onRemove?: (index: number) => void;
  onClearAll: () => void;
  /** Search box, purpose-built facet controls, or anything else that belongs on this line. */
  children?: ReactNode;
  /**
   * Reveals "Clear all" when the list is narrowed by something this bar does not own as a
   * chip — the purpose-built controls passed as `children`. `useListState().active`
   * supplies it.
   */
  active?: boolean;
  className?: string;
}

export function FilterBar({
  fields = [],
  filters = [],
  onAdd,
  onRemove,
  onClearAll,
  children,
  active,
  className,
}: FilterBarProps) {
  const { t } = useTranslation();
  // Explicit, because the popover is portalled to <body> and Radix defaults to "ltr".
  const dir = useChromeDirection();
  const [open, setOpen] = useState(false);

  return (
    <div
      role="search"
      aria-label={t('ui.filters')}
      className={cn('flex flex-wrap items-center gap-xs', className)}
    >
      {children}

      {filters.length > 0 && (
        <ul className="flex flex-wrap items-center gap-2xs" aria-label={t('ui.activeFilters')}>
          {filters.map((filter, index) => (
            <li key={`${serializeFilter(filter)}-${index}`}>
              <span className="inline-flex items-center gap-2xs rounded-pill border-[0.5px] border-hairline-strong bg-canvas-bone ps-sm pe-2xs py-2xs text-small text-ink-graphite">
                {chipLabel(filter, fields, t)}
                <button
                  type="button"
                  onClick={() => onRemove?.(index)}
                  aria-label={t('ui.removeFilter', { filter: chipLabel(filter, fields, t) })}
                  /* 24px, not 20: this is the only way to drop a filter with a
                   pointer, it sits on every list screen in the console, and 24 is
                   the floor WCAG 2.2 target size (minimum) sets. */
                  className="inline-flex h-[24px] w-[24px] items-center justify-center rounded-pill text-ink-mid transition-colors duration-almanac hover:bg-canvas-deep-sand hover:text-ink-deep focus-visible:shadow-focus"
                >
                  <span aria-hidden>×</span>
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Offered only when there is something to add. A screen whose filters are
          all purpose-built controls has no ad-hoc field set, and an "Add filter"
          button opening an empty form is a dead end of exactly the kind this
          phase exists to remove. */}
      {fields.length > 0 && onAdd && (
        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger asChild>
            <Button variant="ghost" size="sm">
              {t('ui.addFilter')}
            </Button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              dir={dir}
              align="start"
              sideOffset={6}
              className="z-50 w-[300px] rounded-md border-[0.5px] border-hairline bg-canvas-paper p-md shadow-nav-scrolled"
            >
              <AddFilterForm
                fields={fields}
                onSubmit={(filter) => {
                  onAdd(filter);
                  setOpen(false);
                }}
              />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      )}

      {(filters.length > 0 || active) && (
        <Button variant="ghost" size="sm" onClick={onClearAll}>
          {t('ui.clearAll')}
        </Button>
      )}
    </div>
  );
}

const controlClass =
  'w-full rounded-sm border-[0.5px] border-hairline-strong bg-canvas-paper px-sm py-xs text-small ' +
  'text-ink-graphite focus:border-accent-blue focus-visible:shadow-focus';

function AddFilterForm({
  fields,
  onSubmit,
}: {
  fields: FilterFieldDef[];
  onSubmit: (filter: Filter) => void;
}) {
  const { t } = useTranslation();
  const [fieldId, setFieldId] = useState(fields[0]?.id ?? '');
  const field = fields.find((f) => f.id === fieldId) ?? fields[0];
  const operators = field ? operatorsFor(field) : [];
  const [operator, setOperator] = useState<FilterOperator>(operators[0] ?? 'is');
  const [value, setValue] = useState('');

  if (!field) return null;

  const needsValue = !VALUELESS_OPERATORS.has(operator);
  const canSubmit = !needsValue || value.trim() !== '';

  const changeField = (id: string) => {
    const next = fields.find((f) => f.id === id);
    setFieldId(id);
    // The operator list changes with the field; keeping a now-invalid operator
    // would produce a chip that parseFilter throws away on the next page load.
    if (next) setOperator(operatorsFor(next)[0]);
    setValue('');
  };

  return (
    <form
      className="flex flex-col gap-sm"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onSubmit({ field: fieldId, operator, value: needsValue ? value.trim() : '' });
        setValue('');
      }}
    >
      <label className="flex flex-col gap-2xs">
        <span className="text-label uppercase tracking-[0.1em] text-ink-mute">
          {t('ui.filterField')}
        </span>
        <select
          className={controlClass}
          value={fieldId}
          onChange={(e) => changeField(e.target.value)}
        >
          {fields.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-2xs">
        <span className="text-label uppercase tracking-[0.1em] text-ink-mute">
          {t('ui.filterOperator')}
        </span>
        <select
          className={controlClass}
          value={operator}
          onChange={(e) => setOperator(e.target.value as FilterOperator)}
        >
          {operators.map((op) => (
            <option key={op} value={op}>
              {t(`ui.filterOp_${op}`)}
            </option>
          ))}
        </select>
      </label>

      {needsValue && (
        <label className="flex flex-col gap-2xs">
          <span className="text-label uppercase tracking-[0.1em] text-ink-mute">
            {t('ui.filterValue')}
          </span>
          {field.type === 'select' ? (
            <select
              className={controlClass}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            >
              <option value="">{t('ui.selectPlaceholder')}</option>
              {field.options?.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              className={controlClass}
              type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          )}
        </label>
      )}

      <Button type="submit" size="sm" disabled={!canSubmit}>
        {t('ui.applyFilter')}
      </Button>
    </form>
  );
}

// --------------------------------------------------- lists the console holds in full

/**
 * Does `value` satisfy one chip? For a list the console holds in full and filters itself
 * (the server's list endpoints take named parameters, not chips). Text comparisons are
 * case-insensitive; `before`/`after` compare numbers numerically and anything else as text,
 * which orders ISO dates correctly.
 */
export function matchesFilter(value: string | number | null | undefined, filter: Filter): boolean {
  const text = value == null ? '' : String(value);
  const needle = filter.value.toLocaleLowerCase();
  const hay = text.toLocaleLowerCase();
  const asNumber = (s: string) => (s.trim() === '' ? NaN : Number(s));
  const compare = () => {
    const a = asNumber(text);
    const b = asNumber(filter.value);
    return Number.isNaN(a) || Number.isNaN(b) ? text.localeCompare(filter.value) : a - b;
  };
  switch (filter.operator) {
    case 'is':
      return hay === needle;
    case 'is_not':
      return hay !== needle;
    case 'contains':
      return hay.includes(needle);
    case 'is_empty':
      return text.trim() === '';
    case 'is_not_empty':
      return text.trim() !== '';
    case 'before':
      return text !== '' && compare() < 0;
    case 'after':
      return text !== '' && compare() > 0;
    default:
      return true;
  }
}

/** Rows that satisfy every chip (chips combine with AND). */
export function applyFilters<T>(
  rows: readonly T[],
  filters: readonly Filter[],
  valueOf: (row: T, field: string) => string | number | null | undefined,
): T[] {
  if (filters.length === 0) return [...rows];
  return rows.filter((row) => filters.every((f) => matchesFilter(valueOf(row, f.field), f)));
}
